/**
 * VerifyCertificatePage — Iter387
 *
 * Page publique (sans auth) atteinte via QR code embarqué dans le PDF certificat.
 * URL : /verify-certificate/:certId?sig=<sha256>
 * Backend : GET /api/public/verify-certificate?cid=&sig=
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ShieldCheck, XCircle, ShieldOff, Loader2, ArrowLeft } from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

export default function VerifyCertificatePage() {
  const { certId } = useParams();
  const [params] = useSearchParams();
  const sig = params.get('sig') || '';
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!certId || !sig) {
      setState({ loading: false, data: null, error: 'invalid_format' });
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/public/verify-certificate?cid=${encodeURIComponent(certId)}&sig=${encodeURIComponent(sig)}`);
        if (r.ok) {
          const data = await r.json();
          setState({ loading: false, data, error: null });
        } else if (r.status === 404) {
          setState({ loading: false, data: null, error: 'not_found' });
        } else if (r.status === 400) {
          setState({ loading: false, data: null, error: 'invalid_format' });
        } else {
          setState({ loading: false, data: null, error: 'service_unavailable' });
        }
      } catch {
        setState({ loading: false, data: null, error: 'network_error' });
      }
    })();
  }, [certId, sig]);

  const isRevoked = !!(state.data && state.data.revoked);
  const isAuthentic = !!(state.data && state.data.authentic);

  return (
    <div className="min-h-screen bg-[#0F0E0C] text-[#F3E9C8] flex items-center justify-center p-6" data-testid="verify-certificate-page">
      <div className="max-w-xl w-full">
        <div className="flex items-center gap-2 mb-8">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#C49B2A] font-semibold">
            COACHDIA · Vérification Certificat
          </span>
        </div>

        {state.loading && (
          <div className="flex items-center justify-center py-20" data-testid="verify-certificate-loading">
            <Loader2 className="w-6 h-6 text-[#C49B2A] animate-spin" />
          </div>
        )}

        {!state.loading && isAuthentic && (
          <div className="bg-[#FBF9F3] text-[#1F1A12] rounded-2xl p-8 border border-[#C49B2A]" data-testid="verify-certificate-authentic">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-8 h-8 text-[#92710A]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#92710A] font-semibold">Certificat authentique</div>
                <h1 className="text-2xl font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
                  Formation certifiée
                </h1>
              </div>
            </div>

            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Bénéficiaire</dt>
                <dd className="text-[#1F1A12] font-medium text-right">{state.data.user_name || '—'}</dd>
              </div>
              {state.data.display_number && (
                <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2" data-testid="verify-display-number">
                  <dt className="text-[#6B5E48]">N° officiel</dt>
                  <dd className="text-[#92710A] font-mono font-semibold text-right">{state.data.display_number}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Émis le</dt>
                <dd className="text-[#1F1A12] font-medium text-right">
                  {state.data.issued_at ? new Date(state.data.issued_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                <dt className="text-[#6B5E48]">Piliers validés</dt>
                <dd className="text-[#1F1A12] font-semibold tabular-nums text-right">{state.data.pillars_count}/5</dd>
              </div>
              {state.data.company_id && (
                <div className="flex justify-between gap-4 border-b border-[#E8E2D2] pb-2">
                  <dt className="text-[#6B5E48]">Entreprise</dt>
                  <dd className="text-[#1F1A12] font-medium font-mono text-xs text-right">{state.data.company_id}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-[#6B5E48]">Empreinte SHA-256</dt>
                <dd className="text-[#92710A] font-mono text-xs text-right">{state.data.signature_short}…</dd>
              </div>
            </dl>

            <p className="text-[11px] text-[#A89F8A] mt-6">
              Émis par {state.data.issuer || 'COACHDIA'}. Document signé cryptographiquement (SHA-256).
            </p>
          </div>
        )}

        {!state.loading && isRevoked && (
          <div className="bg-[#1A0E0E] border border-[#5C1F1F] rounded-2xl p-8 text-[#F5C7C7]" data-testid="verify-certificate-revoked">
            <ShieldOff className="w-7 h-7 text-[#E89090] mb-3" />
            <h1 className="text-xl font-light text-[#F5C7C7] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Certificat révoqué
            </h1>
            <p className="text-sm text-[#E0BABA] mb-3">
              Ce certificat a été révoqué par l'émetteur et n'est plus valide.
            </p>
            {state.data?.revoked_at && (
              <p className="text-xs text-[#D0A8A8]">
                Révoqué le {new Date(state.data.revoked_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            )}
            {state.data?.revoked_reason && (
              <p className="text-xs text-[#D0A8A8] mt-2 italic">Motif : {state.data.revoked_reason}</p>
            )}
            {state.data?.display_number && (
              <p className="text-xs text-[#D0A8A8] mt-3 font-mono" data-testid="verify-display-number-revoked">
                N° {state.data.display_number}
              </p>
            )}
            <p className="text-[10px] text-[#A88080] mt-6 font-mono">SHA-256 : {state.data?.signature_short}…</p>
          </div>
        )}

        {!state.loading && state.error === 'not_found' && (
          <div className="bg-[#1A0E0E] border border-[#5C1F1F] rounded-2xl p-8 text-[#F5C7C7]" data-testid="verify-certificate-not-found">
            <XCircle className="w-7 h-7 text-[#E89090] mb-3" />
            <h1 className="text-xl font-light text-[#F5C7C7] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Authenticité non vérifiée
            </h1>
            <p className="text-sm text-[#E0BABA]">
              Ce certificat ne correspond à aucun document COACHDIA enregistré. La signature ou l'identifiant est invalide.
            </p>
          </div>
        )}

        {!state.loading && state.error === 'invalid_format' && (
          <div className="bg-[#1A0E0E] border border-[#5C1F1F] rounded-2xl p-8 text-[#F5C7C7]" data-testid="verify-certificate-invalid">
            <XCircle className="w-7 h-7 text-[#E89090] mb-3" />
            <h1 className="text-xl font-light text-[#F5C7C7] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Lien invalide
            </h1>
            <p className="text-sm text-[#E0BABA]">
              Le format du lien de vérification n'est pas reconnu. Scanne à nouveau le QR depuis le PDF.
            </p>
          </div>
        )}

        {!state.loading && (state.error === 'service_unavailable' || state.error === 'network_error') && (
          <div className="bg-[#1F1810] border border-[#3F3520] rounded-2xl p-8 text-[#EAD18E]" data-testid="verify-certificate-error">
            <Loader2 className="w-7 h-7 text-[#EAD18E] mb-3" />
            <h1 className="text-xl font-light text-[#EAD18E] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Service indisponible
            </h1>
            <p className="text-sm text-[#D6BB73]">Réessaye dans quelques instants.</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[#A89F8A] hover:text-[#F3E9C8] transition-colors" data-testid="verify-certificate-home">
            <ArrowLeft className="w-3 h-3" /> Retour COACHDIA
          </Link>
        </div>
      </div>
    </div>
  );
}
